import { Component, Input, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { firstValueFrom } from 'rxjs';
import { ZoneService } from '../../../services/zone.service';
import { MapService } from '../../../services/map.service';
import { ZonePriceResponseDto } from '../../../dto/zone.price.response.dto';

type MarkerVM = {
    position: google.maps.LatLngLiteral;
    options: google.maps.MarkerOptions;
};

@Component({
    selector: 'app-zone-price-map-dialog',
    templateUrl: './zone-price-map-dialog.component.html',
    styleUrls: ['./zone-price-map-dialog.component.scss']
})
export class ZonePriceMapDialogComponent {

    @Input() cityId!: number;
    @Input() segmentId!: number;
    @Input() adId!: number;

    // Filtros opcionales desde el padre
    @Input() departmentId?: number; // ctDepartment
    @Input() zoneId?: number;       // tu zona → pill “primary”

    // Para el panel de info (se muestran desde el padre /price)
    @Input() segmentLabel?: string;
    @Input() offerLabel?: string;

    @ViewChild(GoogleMap) map!: GoogleMap;

    showDialog = false;
    showInfoPanel = true; // colapsable en mobile

    /** Resumen de precios por m² (USD) para el panel "Compara propiedades similares". */
    priceStats: { min: number; max: number; avg: number; count: number } | null = null;

    markers: MarkerVM[] = [];
    center: google.maps.LatLngLiteral = { lat: -16.5, lng: -68.15 };
    zoom = 13;
    mapOptions: google.maps.MapOptions = {
        gestureHandling: 'greedy',
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false
    };

    constructor(private zoneService: ZoneService, private mapService: MapService) {}

    openDialog(): void {
        this.showDialog = true;
        this.loadZones();
    }

    closeDialog(): void {
        this.showDialog = false;
        this.markers = [];
    }

    toggleInfoPanel(): void { this.showInfoPanel = !this.showInfoPanel; }

    // Valores formateados (USD) para el panel de info
    get statMin(): string { return this.priceStats ? this.formatMoney(this.priceStats.min) : ''; }
    get statMax(): string { return this.priceStats ? this.formatMoney(this.priceStats.max) : ''; }
    get statAvg(): string { return this.priceStats ? this.formatMoney(this.priceStats.avg) : ''; }

    private async loadZones() {
        this.markers = [];
        const bounds = new google.maps.LatLngBounds();

        this.zoneService.getZonePriceList(this.cityId, this.segmentId, this.adId)
            .subscribe(async (zones: ZonePriceResponseDto[]) => {

                const effectiveDept =
                    this.departmentId ??
                    zones.find(z => this.cityId ? z.cityId === this.cityId : true)?.ctDepartment ??
                    zones[0]?.ctDepartment;

                const effectiveCity = this.cityId ?? zones[0]?.cityId;
                const effectiveZone = this.zoneId;

                const filtered = zones.filter(z =>
                    (effectiveDept == null || z.ctDepartment === effectiveDept) &&
                    (effectiveCity == null || z.cityId === effectiveCity) &&
                    (effectiveZone == null || z.zoneId === effectiveZone || true) // mostramos todas, pero marcamos primary la tuya
                );

                // Resumen para el panel "Compara propiedades similares" (precio por m² en USD).
                const prices = filtered
                    .map(z => z.price)
                    .filter((p): p is number => p != null && !isNaN(Number(p)))
                    .map(Number);
                this.priceStats = prices.length ? {
                    min: Math.min(...prices),
                    max: Math.max(...prices),
                    avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
                    count: prices.length
                } : null;

                for (const z of filtered) {
                    // 1) Obtener coords
                    let lat = z.latitude ? parseFloat(String(z.latitude)) : undefined;
                    let lng = z.longitude ? parseFloat(String(z.longitude)) : undefined;

                    if (!lat || !lng) {
                        try {
                            const query = z.cityName
                                ? `${z.zoneName}, ${z.cityName}, Bolivia`
                                : `${z.zoneName}, Bolivia`;
                            const coords = await firstValueFrom(this.mapService.getCoordinatesFromName(query));
                            if (coords) { lat = coords.lat; lng = coords.lng; }
                        } catch {}
                    }
                    if (!lat || !lng) continue;

                    // 2) Texto UX
                    const price = z.price ?? null;
                    const priceText = price != null ? `${this.formatMoney(price)} USD` : 'N/D';
                    const label = `${z.zoneName} · ${priceText}`;

                    // 3) Pill dinámico (primary si es tu zona)
                    const isPrimary = this.zoneId != null && z.zoneId === this.zoneId;
                    const pill = this.buildPricePill(label, { primary: isPrimary });

                    const position = { lat, lng };
                    bounds.extend(position);

                    // 4) Marker
                    this.markers.push({
                        position,
                        options: {
                            icon: {
                                url: pill.url,
                                scaledSize: new google.maps.Size(pill.width, pill.height),
                                anchor: new google.maps.Point(pill.width / 2, pill.height / 2)
                            },
                            optimized: false,
                            zIndex: isPrimary ? 100 : 10
                        }
                    });
                }

                if (!bounds.isEmpty() && this.map?.googleMap) {
                    this.map.googleMap.fitBounds(bounds, 60);
                }
            });
    }

    private formatMoney(n: number): string {
        return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }

    /**
     * Genera un pill SVG del tamaño exacto en función del texto
     * primary = color acento para tu zona; default = gris oscuro
     */
    private buildPricePill(
        text: string,
        opts: { primary?: boolean; fontSize?: number; px?: number; py?: number; minWidth?: number } = {}
    ): { url: string; width: number; height: number } {
        const safe = String(text)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');

        const fontSize = opts.fontSize ?? 15; // legible en mobile
        const px = opts.px ?? 18;             // padding horizontal
        const py = opts.py ?? 10;             // padding vertical
        const minWidth = opts.minWidth ?? 180;

        // medir ancho del texto
        const ctx = document.createElement('canvas').getContext('2d')!;
        ctx.font = `700 ${fontSize}px Inter, Arial, sans-serif`;
        const tw = Math.ceil(ctx.measureText(safe).width);

        const width = Math.max(minWidth, tw + px * 2);
        const height = fontSize + py * 2;
        const rx = Math.min(22, height / 2);

        const bg = opts.primary ? '#8FB271' : '#111';         // acento vs gris
        const fg = '#ffffff';

        const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="150%" height="150%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000" flood-opacity="0.25"/>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <rect x="0" y="0" rx="${rx}" ry="${rx}" width="${width}" height="${height}" fill="${bg}" opacity="0.95"/>
          <text x="50%" y="50%" font-family="Inter, Arial, sans-serif"
                font-size="${fontSize}" font-weight="700" fill="${fg}"
                text-anchor="middle" dominant-baseline="central">${safe}</text>
        </g>
      </svg>
    `;
        const encoded = encodeURIComponent(svg);
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encoded}`,
            width,
            height
        };
    }
}
